import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, CheckCircle, AlertTriangle, Clock, Download, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Compliance = ({ user }) => {
  const [complianceData, setComplianceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComplianceData();
  }, []);

  const fetchComplianceData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/compliance`);
      setComplianceData(response.data);
    } catch (error) {
      console.error('Error fetching compliance data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-amber-600" />;
      case 'requires_review':
        return <AlertTriangle className="w-5 h-5 text-rose-600" />;
      default:
        return <Shield className="w-5 h-5 text-slate-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'pending':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'requires_review':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const exportToCSV = async () => {
    try {
      const batchesRes = await axios.get(`${BACKEND_URL}/api/batches`);
      const batches = batchesRes.data;

      const csvRows = [
        ['Batch Number', 'Product', 'Origin', 'Standard', 'Status', 'Last Audit', 'Integrity Score'].join(',')
      ];

      batches.forEach(batch => {
        batch.compliance.forEach(comp => {
          const row = [
            batch.batch_number,
            batch.product_name,
            batch.origin.name,
            comp.standard,
            comp.status,
            new Date(comp.last_audit).toLocaleDateString('en-NO'),
            batch.integrity_score
          ].join(',');
          csvRows.push(row);
        });
      });

      // Add footer disclaimer
      csvRows.push('');
      csvRows.push('Generated by the PureSustain platform for traceability and reporting support. This document does not replace regulatory filings or accredited certification.');

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance-report-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export CSV');
    }
  };

  const printReport = () => {
    window.print();
    toast.success('Opening print dialog...');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading compliance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="compliance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
            Compliance Dashboard
          </h1>
          <p className="text-lg text-slate-600">
            CSRD, ISO 14001, and food safety compliance overview
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToCSV} data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={printReport} data-testid="print-report-button">
            <FileText className="w-4 h-4 mr-2" />
            Print Report
          </Button>
        </div>
      </div>

      {/* Overview Card */}
      <div className="bg-white rounded-xl p-8 border border-slate-200">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-4 bg-slate-100 rounded-xl">
            <Shield className="w-8 h-8 text-slate-700" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
              Compliance Overview
            </h2>
            <p className="text-slate-600">Across {complianceData?.total_batches || 0} product batches</p>
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-6">
          <p className="text-sm text-slate-600 mb-2">About Compliance Standards</p>
          <p className="text-slate-700 leading-relaxed">
            <strong>CSRD (Corporate Sustainability Reporting Directive)</strong> ensures transparent sustainability reporting across EU operations.
            <strong className="ml-2">ISO 14001</strong> certifies environmental management systems.
            Additional standards like MSC, HACCP, and Organic certifications ensure product quality and sustainability.
          </p>
        </div>
      </div>

      {/* Standards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="compliance-standards-grid">
        {complianceData?.standards.map((standard, idx) => {
          const complianceRate = standard.total > 0 ? (standard.compliant / standard.total) * 100 : 0;

          return (
            <div
              key={idx}
              data-testid={`compliance-card-${standard.standard}`}
              className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow duration-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-1">{standard.standard}</h3>
                  <p className="text-sm text-slate-600">{standard.total} batches tracked</p>
                </div>
                <Shield className="w-6 h-6 text-slate-600" />
              </div>

              {/* Compliance Rate Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Compliance Rate</span>
                  <span className="text-lg font-bold text-slate-800">{complianceRate.toFixed(0)}%</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-600 rounded-full"
                    style={{ width: `${complianceRate}%` }}
                  ></div>
                </div>
                <p className="text-xs text-slate-500 mt-2 italic">
                  Compliance status reflects data completeness and traceability within the platform. Regulatory certification remains the responsibility of accredited authorities.
                </p>
              </div>

              {/* Status Breakdown */}
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 px-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-700" />
                    <span className="text-sm text-emerald-800 font-medium">Compliant</span>
                  </div>
                  <span className="text-sm font-semibold text-emerald-800">{standard.compliant}</span>
                </div>

                {standard.pending > 0 && (
                  <div className="flex items-center justify-between py-2 px-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-700" />
                      <span className="text-sm text-amber-800 font-medium">Pending</span>
                    </div>
                    <span className="text-sm font-semibold text-amber-800">{standard.pending}</span>
                  </div>
                )}

                {standard.requires_review > 0 && (
                  <div className="flex items-center justify-between py-2 px-3 bg-rose-50 rounded-lg border border-rose-200">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-rose-700" />
                      <span className="text-sm text-rose-800 font-medium">Requires Review</span>
                    </div>
                    <span className="text-sm font-semibold text-rose-800">{standard.requires_review}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Blockchain Records Note - Footnote */}
      <div className="mt-6 pt-4 border-t border-slate-200">
        <p className="text-xs text-slate-500 leading-snug">
          <strong className="text-slate-600">Blockchain-Verified Records and Audit Trails:</strong> All compliance records are cryptographically verified and stored immutably. Each audit, certification, and quality check is timestamped and linked to the batch's blockchain record, ensuring complete transparency and tamper-proof documentation for regulatory reporting.
        </p>
      </div>
    </div>
  );
};

export default Compliance;
